import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrgCurrency } from '../../../../hooks/useOrgCurrency.js';
import { Wallet, FileText, CreditCard, ChevronDown, ChevronUp, Info } from 'lucide-react';

/**
 * Financing type options — labels resolved at render via t() so a language
 * change updates them (module-level constant would freeze the FR label).
 */
const FINANCING_TYPES = [
  { value: 'cash', icon: Wallet },
  { value: 'lease', icon: FileText },
  { value: 'loan', icon: CreditCard }
];

/** Leasing sub-types */
const LEASING_TYPES = ['lld', 'lcd', 'loa', 'other'];

/** Credit sub-types */
const CREDIT_TYPES = ['auto', 'affecte', 'ballon', 'other'];

/** Financing options for leasing */
const LEASING_OPTIONS = ['maintenance', 'tires', 'assistance', 'replacement', 'insurance', 'gps_tracker'];

/** Financing options for credit */
const CREDIT_OPTIONS = ['borrower_insurance', 'gap_insurance', 'extended_warranty'];

/**
 * StepFinancing - Step 3 of VehicleLifecycleWorkflow
 *
 * Captures complete vehicle financing/acquisition details per specification:
 * - Acquisition type (cash, lease, loan)
 * - Credit: Type, dates, amount, monthly payment, TAEG, options (HT/TVA/TTC)
 * - Leasing: Type, duration, dates, annual km, deposit, first payment, options (HT/TVA/TTC)
 */
function StepFinancing({ formData, updateFormData }) {
  const { t } = useTranslation();
  // Devise de la filiale active - un seul outil monetaire.
  const { moneyScope, scopeSymbol } = useOrgCurrency();
  const [showOptions, setShowOptions] = useState(false);

  // Calculate TVA (20%) from HT
  const calculateTVA = (ht) => {
    const htValue = parseFloat(ht) || 0;
    return (htValue * 0.2).toFixed(2);
  };

  // Calculate TTC from HT
  const calculateTTC = (ht) => {
    const htValue = parseFloat(ht) || 0;
    return (htValue * 1.2).toFixed(2);
  };

  // Get current financing options (stored as JSON string)
  const financingOptions = useMemo(() => {
    try {
      return JSON.parse(formData.financing_options || '{}');
    } catch {
      return {};
    }
  }, [formData.financing_options]);

  // Toggle a financing option
  const toggleOption = (key) => {
    const updated = { ...financingOptions, [key]: !financingOptions[key] };
    updateFormData('financing_options', JSON.stringify(updated));
  };

  // Get options list based on financing type
  const availableOptions = formData.acquisition_type === 'lease' ? LEASING_OPTIONS : CREDIT_OPTIONS;

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-900 mb-4">{t('vehicles.wizard.financing.title')}</h3>

      {/* Financing Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">{t('vehicles.wizard.financing.financingTypeLabel')}</label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {FINANCING_TYPES.map(type => (
            <button
              key={type.value}
              type="button"
              onClick={() => updateFormData('acquisition_type', type.value)}
              className={`p-4 border-2 rounded-lg flex flex-col items-center gap-2 transition-all ${
                formData.acquisition_type === type.value
                  ? 'border-amber-500 bg-amber-50 text-amber-700'
                  : 'border-gray-300 hover:border-amber-300'
              }`}
            >
              <type.icon className="w-8 h-8" />
              <span className="font-medium text-sm">{t(`vehicles.wizard.financing.types.${type.value}.label`)}</span>
              <span className="text-xs text-gray-500 text-center">{t(`vehicles.wizard.financing.types.${type.value}.description`)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Cash Purchase Fields */}
      {formData.acquisition_type === 'cash' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-4">
          <h4 className="font-semibold text-green-900">{t('vehicles.wizard.financing.cash.title')}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.financing.cash.purchasePriceExcl', { symbol: scopeSymbol })}</label>
              <input
                type="number"
                value={formData.purchase_price || ''}
                onChange={(e) => updateFormData('purchase_price', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="Ex: 20833"
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.financing.cash.purchasePriceIncl', { symbol: scopeSymbol })}</label>
              <input
                type="text"
                value={calculateTTC(formData.purchase_price)}
                readOnly
                className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600"
              />
              <p className="text-xs text-gray-500 mt-1">{t('vehicles.wizard.financing.cash.vatAmount', { amount: moneyScope(calculateTVA(formData.purchase_price)) })}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.financing.cash.purchaseDate')}</label>
              <input
                type="date"
                value={formData.purchase_date || ''}
                onChange={(e) => updateFormData('purchase_date', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                required
              />
            </div>
          </div>
        </div>
      )}

      {/* Credit Fields */}
      {formData.acquisition_type === 'loan' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
          <h4 className="font-semibold text-blue-900">{t('vehicles.wizard.financing.credit.title')}</h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.financing.credit.creditType')}</label>
              <select
                value={formData.financing_type || 'auto'}
                onChange={(e) => updateFormData('financing_type', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {CREDIT_TYPES.map(value => (
                  <option key={value} value={value}>{t(`vehicles.wizard.financing.creditTypes.${value}`)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.financing.credit.financedAmount', { symbol: scopeSymbol })}</label>
              <input
                type="number"
                value={formData.financed_amount || ''}
                onChange={(e) => updateFormData('financed_amount', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ex: 25000"
                min="0"
                step="0.01"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.financing.credit.startDate')}</label>
              <input
                type="date"
                value={formData.financing_start_date || ''}
                onChange={(e) => updateFormData('financing_start_date', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.financing.credit.endDate')}</label>
              <input
                type="date"
                value={formData.financing_end_date || ''}
                onChange={(e) => updateFormData('financing_end_date', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.financing.credit.monthlyPayment', { symbol: scopeSymbol })}</label>
              <input
                type="number"
                value={formData.monthly_payment || ''}
                onChange={(e) => updateFormData('monthly_payment', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ex: 450"
                min="0"
                step="0.01"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.financing.credit.interestRate')}</label>
              <input
                type="number"
                value={formData.interest_rate || ''}
                onChange={(e) => updateFormData('interest_rate', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ex: 4.5"
                min="0"
                max="100"
                step="0.01"
              />
            </div>
          </div>

          {/* Credit Options */}
          <div className="border-t border-blue-200 pt-4 mt-4">
            <button
              type="button"
              onClick={() => setShowOptions(!showOptions)}
              className="flex items-center gap-2 text-blue-700 font-medium text-sm hover:text-blue-900"
            >
              {showOptions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {t('vehicles.wizard.financing.credit.optionsToggle')}
            </button>

            {showOptions && (
              <div className="mt-3 space-y-3">
                {CREDIT_OPTIONS.map(key => (
                  <label key={key} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-blue-100 cursor-pointer hover:bg-blue-50">
                    <input
                      type="checkbox"
                      checked={financingOptions[key] || false}
                      onChange={() => toggleOption(key)}
                      className="w-4 h-4 text-blue-600 rounded mt-0.5"
                    />
                    <div>
                      <span className="font-medium text-gray-900 block">{t(`vehicles.wizard.financing.creditOptions.${key}.label`)}</span>
                      <span className="text-xs text-gray-500">{t(`vehicles.wizard.financing.creditOptions.${key}.description`)}</span>
                    </div>
                  </label>
                ))}

                {/* Options Cost */}
                <div className="grid grid-cols-2 gap-4 mt-4 p-3 bg-gray-50 rounded-lg">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('vehicles.wizard.financing.credit.optionsCostExcl')}</label>
                    <input
                      type="number"
                      value={formData.financing_options_cost_ht || ''}
                      onChange={(e) => updateFormData('financing_options_cost_ht', e.target.value)}
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('vehicles.wizard.financing.credit.optionsCostIncl')}</label>
                    <input
                      type="text"
                      value={calculateTTC(formData.financing_options_cost_ht)}
                      readOnly
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded bg-gray-100 text-gray-600"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Leasing Fields */}
      {formData.acquisition_type === 'lease' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-4">
          <h4 className="font-semibold text-amber-900">{t('vehicles.wizard.financing.lease.title')}</h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.financing.lease.leasingType')}</label>
              <select
                value={formData.financing_type || 'lld'}
                onChange={(e) => updateFormData('financing_type', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              >
                {LEASING_TYPES.map(value => (
                  <option key={value} value={value}>{t(`vehicles.wizard.financing.leasingTypes.${value}`)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.financing.lease.duration')}</label>
              <input
                type="number"
                value={formData.lease_duration_months || ''}
                onChange={(e) => updateFormData('lease_duration_months', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="Ex: 36"
                min="1"
                max="120"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.financing.lease.startDate')}</label>
              <input
                type="date"
                value={formData.financing_start_date || formData.lease_start_date || ''}
                onChange={(e) => {
                  updateFormData('financing_start_date', e.target.value);
                  updateFormData('lease_start_date', e.target.value);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.financing.lease.endDate')}</label>
              <input
                type="date"
                value={formData.financing_end_date || formData.lease_end_date || ''}
                onChange={(e) => {
                  updateFormData('financing_end_date', e.target.value);
                  updateFormData('lease_end_date', e.target.value);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.financing.lease.annualMileage')}</label>
              <input
                type="number"
                value={formData.lease_annual_mileage || ''}
                onChange={(e) => updateFormData('lease_annual_mileage', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="Ex: 30000"
                min="0"
              />
              <p className="text-xs text-gray-500 mt-1">{t('vehicles.wizard.financing.lease.annualMileageHint')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.financing.lease.deposit', { symbol: scopeSymbol })}</label>
              <input
                type="number"
                value={formData.lease_deposit || ''}
                onChange={(e) => updateFormData('lease_deposit', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="Ex: 3000"
                min="0"
                step="0.01"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.financing.lease.firstPayment', { symbol: scopeSymbol })}</label>
              <input
                type="number"
                value={formData.lease_first_payment || ''}
                onChange={(e) => updateFormData('lease_first_payment', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="Ex: 5000"
                min="0"
                step="0.01"
              />
              <p className="text-xs text-gray-500 mt-1">{t('vehicles.wizard.financing.lease.firstPaymentHint')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.financing.lease.monthlyPaymentExcl', { symbol: scopeSymbol })}</label>
              <input
                type="number"
                value={formData.monthly_payment || ''}
                onChange={(e) => updateFormData('monthly_payment', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="Ex: 450"
                min="0"
                step="0.01"
              />
              <p className="text-xs text-gray-500 mt-1">{t('vehicles.wizard.financing.lease.monthlyPaymentInclHint', { amount: moneyScope(calculateTTC(formData.monthly_payment)) })}</p>
            </div>
          </div>

          {/* Leasing Options */}
          <div className="border-t border-amber-200 pt-4 mt-4">
            <button
              type="button"
              onClick={() => setShowOptions(!showOptions)}
              className="flex items-center gap-2 text-amber-700 font-medium text-sm hover:text-amber-900"
            >
              {showOptions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {t('vehicles.wizard.financing.lease.optionsToggle')}
            </button>

            {showOptions && (
              <div className="mt-3 space-y-3">
                <div className="flex items-start gap-2 p-2 bg-amber-100 rounded text-sm text-amber-800">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{t('vehicles.wizard.financing.lease.optionsHint')}</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {LEASING_OPTIONS.map(key => (
                    <label key={key} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-amber-100 cursor-pointer hover:bg-amber-50">
                      <input
                        type="checkbox"
                        checked={financingOptions[key] || false}
                        onChange={() => toggleOption(key)}
                        className="w-4 h-4 text-amber-600 rounded mt-0.5"
                      />
                      <div>
                        <span className="font-medium text-gray-900 block text-sm">{t(`vehicles.wizard.financing.leasingOptions.${key}.label`)}</span>
                        <span className="text-xs text-gray-500">{t(`vehicles.wizard.financing.leasingOptions.${key}.description`)}</span>
                      </div>
                    </label>
                  ))}
                </div>

                {/* Options Cost */}
                <div className="grid grid-cols-2 gap-4 mt-4 p-3 bg-gray-50 rounded-lg">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('vehicles.wizard.financing.lease.optionsCostExclMonthly')}</label>
                    <input
                      type="number"
                      value={formData.financing_options_cost_ht || ''}
                      onChange={(e) => updateFormData('financing_options_cost_ht', e.target.value)}
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-amber-500"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('vehicles.wizard.financing.lease.optionsCostIncl')}</label>
                    <input
                      type="text"
                      value={calculateTTC(formData.financing_options_cost_ht)}
                      readOnly
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded bg-gray-100 text-gray-600"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Total Cost Summary */}
          {(() => {
            const baseMonthly = parseFloat(formData.monthly_payment) || 0;
            const optionsMonthly = parseFloat(formData.financing_options_cost_ht) || 0;
            const totalMonthly = baseMonthly + optionsMonthly;
            return (
              <div className="border-t border-amber-200 pt-4 mt-4">
                <h5 className="font-medium text-amber-900 mb-3">{t('vehicles.wizard.financing.lease.summaryTitle')}</h5>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div className="bg-white p-3 rounded-lg border border-amber-100">
                    <span className="text-gray-500 block text-xs">{t('vehicles.wizard.financing.lease.summaryFirstPayment')}</span>
                    <span className="font-bold text-gray-900">{moneyScope(formData.lease_first_payment || 0)}</span>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-amber-100">
                    <span className="text-gray-500 block text-xs">{t('vehicles.wizard.financing.lease.summaryMonthlyPayment')}</span>
                    <span className="font-bold text-gray-900">{moneyScope(totalMonthly)}</span>
                    {optionsMonthly > 0 && (
                      <span className="block text-[11px] text-gray-500 mt-0.5">
                        {t('vehicles.wizard.financing.lease.summaryOptionsBreakdown', { base: moneyScope(baseMonthly), options: moneyScope(optionsMonthly) })}
                      </span>
                    )}
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-amber-100">
                    <span className="text-gray-500 block text-xs">{t('vehicles.wizard.financing.lease.summaryDeposit')}</span>
                    <span className="font-bold text-gray-900">{moneyScope(formData.lease_deposit || 0)}</span>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-amber-100">
                    <span className="text-gray-500 block text-xs">{t('vehicles.wizard.financing.lease.summaryDuration')}</span>
                    <span className="font-bold text-gray-900">{t('vehicles.wizard.financing.lease.months', { count: formData.lease_duration_months || 0 })}</span>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

export default StepFinancing;
